import {useToast} from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import {required} from "@validations";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import router from "@/router";
import axios from "@axios";
import {computed, nextTick, onMounted, reactive, ref, watch,} from "@vue/composition-api";
import {useUtils as useI18nUtils} from "@core/libs/i18n";

export default function useAlarmViewConfig() {

  const {t} = useI18nUtils();

  const isUpdating = ref(false);
  const idView = ref(null);
  const viewName = ref("");
  const tenants = ref([]);
  const tenantOptions = ref([]);
  const selectedTenant = ref(null);
  const linkOptions = ref([]);

  const nameNav = ref("");

  const users = ref([]);
  const selectedUsers = ref([]);
  const usersOptions = ref([]);
  const usersFilteredOptions = ref([]);

  const isDefault = ref(false);
  const always = ref(true);

  const selectedAlarmStatus = ref([]);
  const selectedAlarmType = ref([]);
  const selectedAlarmLevel = ref([]);

  const alarmStatusOptions = ref([
    { value: 'attending', label: t('action_plan.fields.action_plan_status.options.attending') },
    { value: 'waiting', label: t('action_plan.fields.action_plan_status.options.waiting') },
    { value: 'IN_REVIEW', label: 'En Revisión del supervisor' },
    { value: 'AWAITING_CHANGES', label: 'En Revisión del operador' },
    { value: 'IN_MAINTENANCE', label: 'En flujo de mantenimiento' },
  ]);
  const alarmTypeOptions = ref([]);
  const alarmLevelOptions = ref([]);

  const selectedLayout = ref(null);

  const tagsOptions = ref([]);
  const filters = ref({});

  const user = store.getters["user/getUser"];
  const toast = useToast();
  const {refFormObserver, getValidationState} = formValidation();

  const trans = {
    'alarm_views.messages.success_created': t('alarm_views.messages.success_created'),
    'alarm_views.messages.success_updated': t('alarm_views.messages.success_updated'),
  }

  if (router.currentRoute.params.id_view) {
    nameNav.value = "actions.edit"
  } else {
    nameNav.value = "actions.add"
  }

  const weekdays = reactive({
    0: {text: "sun", data: []},
    1: {text: "mon", data: []},
    2: {text: "tue", data: []},
    3: {text: "wed", data: []},
    4: {text: "thu", data: []},
    5: {text: "fri", data: []},
    6: {text: "sat", data: []},
  });
  const showWeekdaysValidationErrors = ref(false);
  const showLayoutItemsValidationErrors = ref(false);

  // Computed properties ------------------------------------
  const userLayouts = computed(() => store.state.layouts.userLayouts);
  const layoutItems = computed(() => store.getters["alarm_views/layoutItems"] || []);
  const configTabs = computed(() => store.getters["alarm_views/configTabs"] || []);
  const activeConfigTabId = computed(
    () => store.state.alarm_views?.activeConfigTabId || "tab_1"
  );
  const canAddConfigTab = computed(() => store.getters["alarm_views/canAddConfigTab"]);
  const maxConfigTabs = computed(() => store.getters["alarm_views/maxConfigTabs"]);

    // Watchers ------------------------------------
  watch(selectedTenant, async (idTenant) => {
    if (idTenant) {
      selectedUsers.value = [];
      await loadConfigOptions(idTenant);
    } else {
      usersOptions.value = [];
    }
  });

  watch(activeConfigTabId, (tabId) => {
    const tab = configTabs.value.find((t) => t.id === tabId);
    if (tab) selectedLayout.value = tab.id_layout;
  });

  // Methods ------------------------------------

  const addDt = (weekday) => {
    weekdays[weekday].data.push({
      weekday,
      start_at: null,
      end_at: null,
    });
  };

  const removeDt = (weekday, index) => {
    weekdays[weekday].data.splice(index, 1);
  };

  const onLayoutsWidgetReady = () => {
    if (!selectedLayout.value && userLayouts.value.length) {
      selectedLayout.value = userLayouts.value[0].id;
    }
  };

  const onLayoutSelected = (id) => {
    selectedLayout.value = id;
    store.dispatch("alarm_views/setConfigTabLayout", {
      tabId: activeConfigTabId.value,
      id_layout: id,
    });
  };

  const setActiveConfigTab = (tabId) => {
    store.dispatch("alarm_views/setActiveConfigTabId", tabId);
    const tab = configTabs.value.find((t) => t.id === tabId);
    if (tab) selectedLayout.value = tab.id_layout;
  };

  const addConfigTab = () => {
    store.dispatch("alarm_views/addConfigTab");
    const tab = store.getters["alarm_views/activeConfigTab"];
    if (tab) selectedLayout.value = tab.id_layout;
  };

  const removeConfigTab = (tabId) => {
    store.dispatch("alarm_views/removeConfigTab", tabId);
    const tab = store.getters["alarm_views/activeConfigTab"];
    if (tab) selectedLayout.value = tab.id_layout;
  };

  const loadInitData = async () => {
    const tenants = await axios.get("v1/tenant");
    tenantOptions.value = tenants.data.data.map((tenant) => ({
      value: tenant.id,
      label: tenant.name,
    }));
  };

  const loadConfigOptions = (idTenant) => {
    return axios
      .get(`v1/services/views/get-config-options/${idTenant}`)
      .then((resp) => {

        const data = resp.data.data;

        alarmTypeOptions.value = data.alarm_types.map(d => ({
          label: d.label,
          value: d.value
        }));
        alarmLevelOptions.value = data.alarm_levels.map(d => ({
          label: d.label,
          value: d.value
        }));
        usersOptions.value = data.users.map(d => ({...d, label: d.label, value: d.value}));
        tagsOptions.value = data.tags.map(d => ({...d}));

      })
      .catch((error) => {
        console.log(error);
      });
  };

  const loadPages = async () => {
    const {data} = await axios.get(`v1/page`, { params: { id_user: user.id } });
    linkOptions.value = data.data;
  };

  const mapResponseTabsToConfigTabs = (tabs) => {
    if (!Array.isArray(tabs) || tabs.length === 0) return null;
    return tabs.map((t, i) => ({
      id: `tab_${i + 1}`,
      name: t.name || `Tap ${i + 1}`,
      id_layout: t.id_layout ?? null,
      layoutItems: Array.isArray(t.layout_config) ? [...t.layout_config] : [],
    }));
  };

  const loadUpdateData = async (idView) => {
    const { data } = await axios.get(`v1/services/views/get-view/${idView}`);
    const response = data.data;

    viewName.value = response.name;
    isDefault.value = response.is_default === 1;
    always.value = response.always === 1;
    selectedTenant.value = response.id_tenant;
    selectedAlarmStatus.value = response.alarm_status;
    selectedAlarmType.value = response.alarm_type;
    selectedAlarmLevel.value = response.alarm_level;
    (response.datetimes || []).forEach((wkd) => weekdays[wkd.weekday].data.push(wkd));

    const filtersSlot = {};
    const allLayoutItems = response.tabs
      ? response.tabs.flatMap((t) => t.layout_config || [])
      : response.layout_config || [];
    allLayoutItems.forEach((item) => {
      if (item.data?.filters && item.data?.source) {
        filtersSlot[item.data.source] = { ...item.data.filters };
      }
    });
    filters.value = { filters: filtersSlot };

    const tabsToSet = mapResponseTabsToConfigTabs(response.tabs);
    if (tabsToSet) {
      store.dispatch("alarm_views/setConfigTabs", tabsToSet);
      selectedLayout.value = tabsToSet[0]?.id_layout ?? response.id_layout;
    } else {
      store.dispatch("alarm_views/setConfigTabs", [{
        id: "tab_1",
        name: "Tap 1",
        id_layout: response.id_layout ?? null,
        layoutItems: Array.isArray(response.layout_config) ? [...response.layout_config] : [],
      }]);
      selectedLayout.value = response.id_layout;
    }

    nextTick(() => {
      selectedUsers.value = response.users || [];
    });
  };

  const validateDateTimes = () => {

    if (always.value) return true;

    let valid = false;

    for (const key in weekdays) {
      const wkday = weekdays[key];
      if (wkday.data.length > 0) {
        const invalid = wkday.data.filter(item => {
          const start = !item.start_at ? -1 : parseInt(item.start_at.replace(':', ''));
          const end = !item.end_at ? -1 : parseInt(item.end_at.replace(':', ''));

          return start === -1 || end === -1;
        });
        valid = invalid.length === 0;
      }
    }

    return valid;
  }

  const selectAllAlarmLevels = (selectAll = true) => {
    if (selectAll) {
     selectedAlarmLevel.value = alarmLevelOptions.value.map(option => option.value);
     return;
    }

    selectedAlarmLevel.value = [];
  };

  const selectAllAlarmTypes = (selectAll = true) => {
    if (selectAll) {
     selectedAlarmType.value = alarmTypeOptions.value.map(option => option.value);
     return;
    }
    
    selectedAlarmType.value = [];
  };

  const hasAtLeastOneTabWithSlots = () =>
    configTabs.value.some((t) => t.layoutItems && t.layoutItems.length > 0);

  const save = async () => {
    const validation = await refFormObserver.value.validate();
    const weekdaysValidation = validateDateTimes();
    const layoutItemsValidation = hasAtLeastOneTabWithSlots();

    showWeekdaysValidationErrors.value = !weekdaysValidation;
    showLayoutItemsValidationErrors.value = !layoutItemsValidation;

    if (!validation || !weekdaysValidation || !layoutItemsValidation) return;

    const weekdaysData = Object.values(weekdays)
      .filter((wkd) => (wkd.data[0] ? { ...wkd.data[0] } : false))
      .map((wkd) => wkd.data.map((_wkd) => ({ ..._wkd })))
      .flat();

    const tabsPayload = configTabs.value.map((t) => ({
      name: t.name,
      id_layout: t.id_layout,
      layout_config: t.layoutItems || [],
    }));
    const firstTab = configTabs.value[0];

    const data = {
      name: viewName.value,
      id_tenant: selectedTenant.value,
      users: selectedUsers.value,
      alarm_status: selectedAlarmStatus.value,
      alarm_type: selectedAlarmType.value,
      alarm_level: selectedAlarmLevel.value,
      id_layout: firstTab?.id_layout ?? selectedLayout.value,
      layout_config: firstTab?.layoutItems ?? layoutItems.value,
      tabs: tabsPayload,
      always: always.value,
      is_default: isDefault.value,
      datetimes: weekdaysData,
    };

    if (!isUpdating.value) {
      axios.post("v1/services/views", { ...data }).then(({ data: resData }) => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans["alarm_views.messages.success_created"],
            icon: "CheckIcon",
            variant: "success",
          },
        });
        isUpdating.value = true;
        router.push({ path: `/alarm-views/config-view/${resData.data.id}` });
      });
    } else {
      axios.put(`v1/services/views/${idView.value}`, { ...data }).then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans["alarm_views.messages.success_updated"],
            icon: "CheckIcon",
            variant: "success",
          },
        });
      });
    }
  };

  onMounted(async () => {
    await loadInitData();
    await loadPages();
    if (router.currentRoute.params.id_view) {
      isUpdating.value = true;
      idView.value = router.currentRoute.params.id_view;
      loadUpdateData(idView.value);
    } else {
      store.dispatch("alarm_views/setConfigTabs", [
        { id: "tab_1", name: "Tap 1", id_layout: null, layoutItems: [] },
      ]);
      selectedLayout.value = null;
    }
  });

  return {
    isUpdating,
    viewName,

    tenants,
    tenantOptions,
    selectedTenant,
    selectedUsers,
    usersOptions,
    users,
    user,
    tagsOptions,
    filters,
    isDefault,
    always,

    selectedAlarmStatus,
    selectedAlarmType,
    selectedAlarmLevel,

    alarmStatusOptions,
    alarmTypeOptions,
    alarmLevelOptions,

    linkOptions,

    selectedLayout,
    onLayoutsWidgetReady,
    onLayoutSelected,
    configTabs,
    activeConfigTabId,
    canAddConfigTab,
    maxConfigTabs,
    setActiveConfigTab,
    addConfigTab,
    removeConfigTab,

    refFormObserver,
    getValidationState,
    required,

    weekdays,
    showWeekdaysValidationErrors,
    addDt,
    removeDt,
    showLayoutItemsValidationErrors,

    save,

    nameNav,

    // New functions for select all functionality
    selectAllAlarmLevels,
    selectAllAlarmTypes,

    usersFilteredOptions,
  };
}
